import { BehaviorSubject, Subject, of, empty } from 'rxjs';
import StateSubject, { createStateDifferenceObservable } from './StateSubject';
import format from 'date-fns/format';
import { map, filter, delay, concatMap, concat } from 'rxjs/operators';
import djangoFetch from '../lib/fetch/djangoFetch';
import parse from 'date-fns/parse';

class SearchModel {
  constructor(
    searchData,
    availableDatesRanges = [],
    ajaxChangeUrlEndpoint
  ) {
    this.ajaxChangeUrlEndpoint = ajaxChangeUrlEndpoint;
    this._availableDatesRange$ = new BehaviorSubject(availableDatesRanges);
    this.searchData = new StateSubject(searchData);
    this._searchError$ = new Subject();
    this._searchResponse$ = new Subject();
    this.init();
  }

  get availableDatesRange$() {
    return this.searchResponse$
      .pipe(filter((response) => response.seasons !== undefined))
      .pipe(
        map(response => response.seasons.map(range => {
          return {
            startDate: parse(range.startDate, 'yyyy-MM-dd', new Date(), { awareOfUnicodeTokens: true }),
            endDate: parse(range.endDate, 'yyyy-MM-dd', new Date(), {awareOfUnicodeTokens: true}),
          };
        })),
      );
  }

  get searchError$() {
    return this._searchError$;
  }

  get searchResponse$() {
    return this._searchResponse$;
  }

  get searchData$() {
    return this.searchData.stateSubject$;
  }

  get searchDataChange$() {
    return createStateDifferenceObservable(this.searchData.stateSubject$);
  }

  setAvailableDatesRange(availableDatesRange) {
    this.availableDatesRange$.next(availableDatesRange);
  }

  setCampCodes(campCodes) {
    this.searchData.setPartialState({ camp_codes: campCodes });
  }

  setAccommodationType(accommodationType) {
    this.searchData.setPartialState({ acc_type: accommodationType });
  }

  setArrivalDate(date) {
    this.searchData.setPartialState({ arrival: date });
  }

  setDepartureDate(date) {
    this.searchData.setPartialState({ departure: date });
  }

  setArrivalDepartureRange(arrivalDate, departureDate) {
    this.searchData.setPartialState({
      arrival: arrivalDate,
      departure: departureDate,
    });
  }

  setNumOfAdults(numOfAdults) {
    this.searchData.setPartialState({ adults: parseInt(numOfAdults, 10) });
  }

  setChildrenAge(childrenAgeArray) {
    this.searchData.setPartialState({ children: childrenAgeArray });
  }

  setNumOfPets(numOfPets) {
    this.searchData.setPartialState({ pets: parseInt(numOfPets, 10) });
  }

  setPromoCode(promoCode) {
    this.searchData.setPartialState({ promo_code: promoCode });
  }

  clearPromoCode() {
    this.searchData.setPartialState({ promo_code: null });
  }

  init() {
    const delay$ = empty().pipe(delay(200));
    this.searchDataChange$
      .pipe(concatMap(data => of(this.convertSubmittingData(data)).pipe(concat(delay$))))
      .subscribe(event => {
        console.log('change: ' + JSON.stringify(event));
        djangoFetch(this.ajaxChangeUrlEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(event),
        })
          .then(response => {
            if (response.status >= 200 && response.status < 400) {
              return response.json();
            }
            return Promise.reject('Network error');
          })
          .then(json => {
            if (json.error) {
              this.searchError$.next({
                error: true,
                field: 'promo_code',
                message: json.message,
              });
            } else {
              this.searchError$.next({
                error: false,
              });
              this.searchResponse$.next(json);
            }
          })
          .catch(error => error);
      });
  }

  convertSubmittingData(submitData) {
    const convertedData = { ...submitData };
    // converting dates
    ['arrival', 'departure'].forEach(dateField => {
      if (
        submitData[dateField] !== undefined &&
        submitData[dateField] !== null
      ) {
        convertedData[dateField] = format(submitData[dateField], 'yyyy-MM-dd');
      }
    });
    // converting promo code
    if (
      submitData['promo_code'] !== undefined &&
      submitData['promo_code'] !== null
    ) {
      convertedData['promo_code'] = submitData['promo_code'].trim();
      if (convertedData['promo_code'].length === 0) {
        convertedData['promo_code'] = null;
      }
    }
    // converting children
    if (
      submitData['children'] !== undefined &&
      submitData['children'] !== null
    ) {
      convertedData['children'] = submitData['children'].join('-');
    }
    return convertedData;
  }
}

export default SearchModel;



// WEBPACK FOOTER //
// ./src/js/search/SearchModel.js